import { notifyResize } from "#skyward_window";
import { defineComponent, onMounted, onUpdated } from "vue";
import { VueContext } from "../../../types";
import { ClassNamesCollection, classNamesToUniqueList, OptionalVueProp } from "../../core/vueHelpers";
import { classNames } from "../classes";
import LabeledData, { LabeledDataProps } from "./LabeledData";

export enum Layout
{
    Vertical = "vertical",
    Horizontal = "horizontal"
}

export interface LabeledDataListData
{
    id?: string;
    class?: ClassNamesCollection;
    layout?: Layout;
    isForSelector?: boolean;
    labeledData?: LabeledDataProps[];
    noSpacing?: boolean;
    alignLabels?: boolean;
    centered?: boolean;
}

export interface LabeledDataListContext extends VueContext
{
    props: LabeledDataListData;
}

export type LabeledDataListProps = LabeledDataListData & VueContext;

// When using this component in react, <LabeledData> components can be passed as children instead of through the labeledData prop.
// The labeledData prop is still supported in react, but it was designed for receiving data from server-side rendering.
const LabeledDataList = defineComponent<LabeledDataListProps>({
    name: "sky-labeleddatalist",
    inheritAttrs: false,
    props:
    {
        id: OptionalVueProp(String),
        class: OptionalVueProp<ClassNamesCollection>([Object, Array, String]),
        layout: {
            type: String as () => Layout,
            default: Layout.Vertical,
            validator: (value: string) => Object.values(Layout).includes(value as Layout)
        },
        isForSelector: OptionalVueProp(Boolean),
        labeledData: OptionalVueProp<LabeledDataProps[]>(Array),
        noSpacing: OptionalVueProp(Boolean),
        alignLabels: OptionalVueProp(Boolean),
        centered: OptionalVueProp(Boolean)
    },
    setup(props, { slots })
    {
        const { id, class: className, layout, labeledData, isForSelector, noSpacing, alignLabels, centered } = props;

        onMounted(() =>
        {
            notifyResize();
        });

        onUpdated(() =>
        {
            notifyResize();
        });

        return () =>
        {
            const classes = new Set<string>([
                classNames.labeledDataList,
                (layout === Layout.Horizontal) ? classNames.horizontal : classNames.vertical,
                ...classNamesToUniqueList(className)
            ]);

            if (noSpacing)
            {
                classes.add(classNames.noSpacing);
            }

            if (centered)
            {
                classes.add(classNames.centered);
            }

            const labeledDataParts = getLabeledDataRowParts();

            return (
                <dl id={id} class={[...classes]}>
                    {
                        labeledDataParts.map((partGroup, index) =>
                        (
                            <LabeledData
                                key={index}
                                {...partGroup}
                            />
                        ))
                    }
                </dl>
            );
        };

        function getLabeledDataRowParts()
        {
            const labeledDataParts: LabeledDataProps[] = [];

            if (labeledData?.length)
            {
                labeledDataParts.push(...labeledData);
            }

            const children = slots.default?.();
            if (children?.length)
            {
                const labeledDataChildren = children.filter(child => (child.type === LabeledData))
                    .map(child => child.props) as LabeledDataProps[];

                labeledDataParts.push(...labeledDataChildren);
            }

            const labeledDataRows: LabeledDataProps[] = [];
            let previousPart = null;
            for (const labeledDataPart of labeledDataParts)
            {
                if (isForSelector)
                {
                    labeledDataPart.isForSelector = true;
                }

                if (alignLabels)
                {
                    labeledDataPart.useTitleMargin = true;
                }

                if (labeledDataPart.inline && previousPart)
                {
                    labeledDataPart.inline = false;
                    previousPart.inline = true;
                }

                labeledDataRows.push(labeledDataPart);
                previousPart = labeledDataPart;
            }

            return labeledDataRows;
        }
    }
});

export default LabeledDataList;
